<template>
  <div class="flex min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
    <!-- Sidebar -->
    <AcdSidebar :isOpen="isSidebarOpen" />

    <!-- Main Content -->
    <main
      class="flex-1 overflow-x-hidden transition-all duration-300"
      :class="{ 'ml-64': isSidebarOpen, 'ml-0': !isSidebarOpen }"
    >
      <!-- Navbar -->
      <header
        class="fixed top-0 z-50 w-full bg-white dark:bg-gray-800 shadow-sm transition-all duration-300"
      >
        <AcdTopbar
          :page-title="currentPageTitle"
          :is-sidebar-open="isSidebarOpen"
          :is-dark-mode="isDarkMode"
          @toggle-sidebar="toggleSidebar"
          @toggle-dark-mode="toggleDarkMode"
        />
      </header>

      <!-- Content Area -->
      <div class="p-6 mt-2 max-w-full">
        <!-- Top Controls -->
        <div class="flex justify-between items-center mb-8">
          <div class="flex items-center space-x-4">
            <span class="text-gray-600 dark:text-gray-300">
              Last updated: {{ currentDate }}
            </span>
            <button
              @click="refreshData"
              class="p-2 rounded-full bg-white dark:bg-gray-700 shadow hover:bg-gray-100 dark:hover:bg-gray-600"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-600 dark:text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>
        </div>

        <!-- Welcome Profile Card -->
        <div class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md mb-8 border-l-4 border-[#035a76]">
          <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div class="flex items-center gap-4">
              <img
                :src="employeeProfile.photo"
                alt="Employee Profile"
                class="w-20 h-20 rounded-full object-cover border-4 border-[#74bd54]"
              />
              <div>
                <h2 class="text-2xl font-bold text-[#035a76] dark:text-white">
                  Welcome back, {{ user?.name || "User" }}
                </h2>
                <p class="text-gray-600 dark:text-gray-300 mt-1">
                  {{ user?.position }} • {{ user?.department }}
                </p>
                <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Employee ID: {{ user?.employee_code }}
                </p>
                  <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
                   {{ user?.section }} • {{ user?.class }}
                </p>
                  <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
{{ user?.subjects?.map(s => s.name).join(', ') }}
                </p>
              </div>
            </div>

            <div class="grid grid-cols-2 gap-4 text-sm">
              <div class="bg-gray-50 dark:bg-gray-700 px-4 py-3 rounded-lg">
                <p class="text-gray-500 dark:text-gray-300">Attendance</p>
                <p class="font-semibold text-[#035a76] dark:text-white">Present</p>
              </div>
              <div class="bg-gray-50 dark:bg-gray-700 px-4 py-3 rounded-lg">
                <p class="text-gray-500 dark:text-gray-300">Next Meeting</p>
                <p class="font-semibold text-[#035a76] dark:text-white">2:00 PM</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Summary Cards -->
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div
            v-for="(card, index) in summaryCards"
            :key="index"
            class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md border-l-4"
            :class="card.border"
          >
            <div class="flex justify-between items-center">
              <div>
                <p class="text-sm font-medium text-[#035a76] dark:text-white">
                  {{ card.title }}
                </p>
                <p class="text-2xl font-bold text-[#035a76] dark:text-white mt-1">
                  {{ card.value }}
                </p>
              </div>
              <div :class="card.iconBg" class="p-3 rounded-full">
                <component :is="card.icon" class="h-6 w-6" />
              </div>
            </div>
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-2">
              {{ card.description }}
            </p>
          </div>
        </div>

        <!-- Main Sections -->
        <div class="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-8">
          <!-- Tasks -->
          <div class="xl:col-span-2 bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md">
            <div class="flex justify-between items-center mb-4">
              <h2 class="text-lg font-semibold text-[#035a76] dark:text-white">
                My Assigned Tasks
              </h2>
              <button class="text-sm text-blue-600 hover:underline">
                View All
              </button>
            </div>

            <div class="space-y-4">
              <div
                v-for="(task, index) in assignedTasks"
                :key="index"
                class="border dark:border-gray-700 rounded-lg p-4"
              >
                <div class="flex justify-between items-start gap-4">
                  <div>
                    <h3 class="font-semibold text-gray-800 dark:text-white">
                      {{ task.title }}
                    </h3>
                    <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      Deadline: {{ task.deadline }}
                    </p>
                  </div>
                  <span
                    class="text-xs px-3 py-1 rounded-full font-medium"
                    :class="task.priorityClass"
                  >
                    {{ task.priority }}
                  </span>
                </div>

                <div class="mt-4">
                  <div class="flex justify-between text-xs mb-1">
                    <span class="text-gray-500 dark:text-gray-400">Progress</span>
                    <span class="text-gray-700 dark:text-gray-300">{{ task.progress }}%</span>
                  </div>
                  <div class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div
                      class="bg-[#74bd54] h-2 rounded-full"
                      :style="{ width: task.progress + '%' }"
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Notifications -->
          <div class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md">
            <div class="flex justify-between items-center mb-4">
              <h2 class="text-lg font-semibold text-[#035a76] dark:text-white">
                Notifications
              </h2>
              <button class="text-sm text-blue-600 hover:underline">
                View All
              </button>
            </div>

            <div class="space-y-4">
              <div
                v-for="(note, index) in notifications"
                :key="index"
                class="border-l-4 pl-4 py-2"
                :class="note.border"
              >
                <p class="font-medium text-gray-800 dark:text-white">
                  {{ note.title }}
                </p>
                <p class="text-sm text-gray-500 dark:text-gray-400">
                  {{ note.message }}
                </p>
                <p class="text-xs text-gray-400 mt-1">
                  {{ note.time }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Leave + Schedule -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <!-- Leave Requests -->
          <div class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md">
            <div class="flex justify-between items-center mb-4">
              <h2 class="text-lg font-semibold text-[#035a76] dark:text-white">
                Leave Requests
              </h2>
              <button class="bg-[#74bd54] text-white px-4 py-2 rounded-lg text-sm hover:bg-[#63a43f]">
                Request Leave
              </button>
            </div>

            <div class="space-y-4">
              <div
                v-for="(leave, index) in leaveRequests"
                :key="index"
                class="border dark:border-gray-700 rounded-lg p-4"
              >
                <div class="flex justify-between items-center">
                  <div>
                    <p class="font-medium text-gray-800 dark:text-white">
                      {{ leave.type }}
                    </p>
                    <p class="text-sm text-gray-500 dark:text-gray-400">
                      {{ leave.dates }}
                    </p>
                  </div>
                  <span class="text-xs px-3 py-1 rounded-full" :class="leave.statusClass">
                    {{ leave.status }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Upcoming Schedule -->
          <div class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md">
            <h2 class="text-lg font-semibold text-[#035a76] dark:text-white mb-4">
              Upcoming Schedule
            </h2>

            <div class="space-y-4">
              <div
                v-for="(event, index) in upcomingEvents"
                :key="index"
                class="flex items-start gap-4 border-b dark:border-gray-700 pb-4"
              >
                <div class="bg-[#035a76] text-white rounded-lg px-3 py-2 text-center min-w-[60px]">
                  <p class="text-sm font-bold">{{ event.day }}</p>
                  <p class="text-xs">{{ event.month }}</p>
                </div>
                <div>
                  <p class="font-medium text-gray-800 dark:text-white">
                    {{ event.title }}
                  </p>
                  <p class="text-sm text-gray-500 dark:text-gray-400">
                    {{ event.time }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import AcdTopbar from '../../components/Acd/AcdTopbar.vue'
import AcdSidebar from '../../components/Acd/AcdSidebar.vue'

  const user = ref(null);

onMounted(() => {
  const storedUser = localStorage.getItem("user");
  if (storedUser) {
    user.value = JSON.parse(storedUser);
  }
});
const getInitials = (name) => {
  if (!name) return "";

  const parts = name.trim().split(" ");

  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase();
  }

  // First and last name
  return (
    parts[0].charAt(0) +
    parts[parts.length - 1].charAt(0)
  ).toUpperCase();
};

const isSidebarOpen = ref(true)
const isDarkMode = ref(false)
const currentPageTitle = ref('Employee Dashboard')

const employeeProfile = ref({
  name: 'John Doe',
  role: 'Accountant',
  department: 'Finance',
  employeeId: 'EMP001',
  photo: 'https://i.pravatar.cc/150?img=12'
})

const summaryCards = ref([
  {
    title: 'Pending Tasks',
    value: 5,
    description: 'Tasks awaiting completion',
    border: 'border-[#035a76]',
    iconBg: 'bg-blue-100 text-[#035a76]',
    icon: 'div'
  },
  {
    title: 'Completed Tasks',
    value: 18,
    description: 'Successfully completed',
    border: 'border-green-500',
    iconBg: 'bg-green-100 text-green-600',
    icon: 'div'
  },
  {
    title: 'Leave Balance',
    value: '12 Days',
    description: 'Annual leave remaining',
    border: 'border-yellow-500',
    iconBg: 'bg-yellow-100 text-yellow-600',
    icon: 'div'
  },
  {
    title: 'Pending Requests',
    value: 2,
    description: 'Awaiting approval',
    border: 'border-purple-500',
    iconBg: 'bg-purple-100 text-purple-600',
    icon: 'div'
  }
])

const assignedTasks = ref([
  {
    title: 'Prepare lesson plan',
    deadline: 'April 12, 2026',
    priority: 'High',
    priorityClass: 'bg-red-100 text-red-600',
    progress: 70
  },
  {
    title: 'Prepare Ghana Day event',
    deadline: 'April 10, 2026',
    priority: 'Medium',
    priorityClass: 'bg-yellow-100 text-yellow-700',
    progress: 40
  }
])

const notifications = ref([
  {
    title: 'New Task Assigned',
    message: 'Budget review task assigned by Finance Manager.',
    time: '1 hour ago',
    border: 'border-blue-500'
  },
  {
    title: 'Leave Approved',
    message: 'Your annual leave request has been approved.',
    time: 'Yesterday',
    border: 'border-green-500'
  }
])

const leaveRequests = ref([
  {
    type: 'Annual Leave',
    dates: 'May 10 - May 15',
    status: 'Approved',
    statusClass: 'bg-green-100 text-green-600'
  },
  {
    type: 'Sick Leave',
    dates: 'April 18 - April 19',
    status: 'Pending',
    statusClass: 'bg-yellow-100 text-yellow-700'
  }
])

const upcomingEvents = ref([
  {
    day: '12',
    month: 'APR',
    title: 'Finance Team Meeting',
    time: '2:00 PM'
  },
  {
    day: '15',
    month: 'APR',
    title: 'Payroll Submission Deadline',
    time: '5:00 PM'
  }
])

const currentDate = new Date().toLocaleDateString('en-US', {
  year: 'numeric',
  month: 'long',
  day: 'numeric'
})

const toggleSidebar = () => {
  isSidebarOpen.value = !isSidebarOpen.value
}

const toggleDarkMode = () => {
  isDarkMode.value = !isDarkMode.value
}

const refreshData = () => {
  console.log('Refreshing employee dashboard...')
}
</script>